/**
 * YourBuildConfigPanel Component
 *
 * Configuration panel for YourBuild widgets.
 * Allows editing title, description, share button, and add-to-cart button settings.
 * When isFromHeader is true, shows additional Mini Widget configuration section.
 * Uses design system components for consistent styling.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { YourBuildConfigPanelProps } from '../Widgets/types';
import {
  YOUR_BUILD_DEFAULT_CONFIG,
  mergeYourBuildWithDefaults,
} from '../../config/widgetDefaults';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';
import InfoTooltip from 'Components/DesignSystem/Tooltip/InfoTooltip';

const DEBOUNCE_DELAY = 500;

const MINI_WIDGET_ON_CLICK_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'popup', label: 'Popup' },
  { value: 'sidepanel', label: 'Sidepanel' },
  { value: 'fullscreen', label: 'Full Screen' },
];

interface YourBuildConfigPanelExtendedProps extends YourBuildConfigPanelProps {
  id?: string;
  isFromHeader?: boolean;
}

export const YourBuildConfigPanel = ({
  id,
  config,
  onChange,
  isFromHeader = false,
}: YourBuildConfigPanelExtendedProps) => {
  const baseId = id || 'your-build-config-panel';
  // Merge config with defaults
  const [localConfig, setLocalConfig] = useState(
    mergeYourBuildWithDefaults(config || YOUR_BUILD_DEFAULT_CONFIG)
  );

  const descriptionRef = useRef<HTMLDivElement>(null);

  // Sync with external config changes
  useEffect(() => {
    const merged = mergeYourBuildWithDefaults(config || YOUR_BUILD_DEFAULT_CONFIG);
    setLocalConfig(merged);
  }, [config]);

  // Sync description editor — also depends on showDescription so the div gets
  // repopulated when it remounts after being toggled off and back on.
  useEffect(() => {
    if (descriptionRef.current && descriptionRef.current.innerHTML !== localConfig.description) {
      descriptionRef.current.innerHTML = localConfig.description;
    }
  }, [localConfig.description, localConfig.showDescription]);

  // Change handlers — onChange propagates to ConfigPanel which saves to sharedWidgets
  const handleChange = useCallback(
    (key: keyof typeof localConfig, value: unknown) => {
      const newConfig = { ...localConfig, [key]: value };
      setLocalConfig(newConfig);
      onChange(newConfig);
    },
    [localConfig, onChange]
  );

  const handleShareButtonChange = useCallback(
    (key: string, value: unknown) => {
      const newConfig = {
        ...localConfig,
        shareButtonConfig: {
          ...localConfig.shareButtonConfig,
          [key]: value,
        },
      };
      setLocalConfig(newConfig);
      onChange(newConfig);
    },
    [localConfig, onChange]
  );

  const handleAddToCartButtonChange = useCallback(
    (key: string, value: unknown) => {
      const newConfig = {
        ...localConfig,
        addToCartButtonConfig: {
          ...localConfig.addToCartButtonConfig,
          [key]: value,
        },
      };
      setLocalConfig(newConfig);
      onChange(newConfig);
    },
    [localConfig, onChange]
  );

  // Debounced inputs using the hook
  const titleInput = useDebouncedInput(
    localConfig.title,
    (value) => handleChange('title', value),
    DEBOUNCE_DELAY
  );

  const shareButtonLabelInput = useDebouncedInput(
    localConfig.shareButtonConfig.buttonLabel,
    (value) => handleShareButtonChange('buttonLabel', value),
    DEBOUNCE_DELAY
  );

  const addToCartButtonLabelInput = useDebouncedInput(
    localConfig.addToCartButtonConfig.buttonLabel,
    (value) => handleAddToCartButtonChange('buttonLabel', value),
    DEBOUNCE_DELAY
  );

  const addingToCartMessageInput = useDebouncedInput(
    localConfig.addToCartButtonConfig.addingToCartMessage,
    (value) => handleAddToCartButtonChange('addingToCartMessage', value),
    DEBOUNCE_DELAY
  );

  const miniWidgetTextInput = useDebouncedInput(
    localConfig.miniWidgetText,
    (value) => handleChange('miniWidgetText', value),
    DEBOUNCE_DELAY
  );

  const descriptionTitleInput = useDebouncedInput(
    localConfig.descriptionTitle,
    (value) => handleChange('descriptionTitle', value),
    DEBOUNCE_DELAY
  );

  // Rich text formatting
  const handleDescriptionFormat = (command: string) => {
    if (descriptionRef.current) {
      descriptionRef.current.focus();

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        document.execCommand(command, false, '');

        descriptionRef.current.focus();

        try {
          selection.removeAllRanges();
          selection.addRange(range);
        } catch (e) {
          console.error('Range restoration failed, keeping focus', e);
        }
      } else {
        document.execCommand(command, false, '');
        descriptionRef.current.focus();
      }
    }
  };

  const handleDescriptionChange = (e: React.FormEvent<HTMLDivElement>) => {
    const htmlContent = e.currentTarget.innerHTML;
    handleChange('description', htmlContent);
  };

  return (
    <div id={baseId} data-testid={baseId} className="tw-space-y-6">
      <div className="tw-p-1">

        {/* Mini Widget Configuration - only shown when accessed from header */}
        {isFromHeader && (
          <div id={`${baseId}-mini-widget-section`} className="tw-mb-4 tw-space-y-3">
            <FieldTitle className="tw-bg-surface-sunken tw-px-1 tw-py-2" label="Mini Widget" size="sm" infoMessage="Mini Widget Configuration" />

            <div id={`${baseId}-mini-widget-show-text`}>
              <Checkbox
                label="Show Text"
                checked={localConfig.miniWidgetShowText}
                onChange={(checked) => handleChange('miniWidgetShowText', checked)}
              />
            </div>

            {localConfig.miniWidgetShowText && (
              <div id={`${baseId}-mini-widget-text`} className="tw-pl-4 tw-border-l-2 tw-border-edge">
                <TextField
                  label=""
                  value={miniWidgetTextInput.localValue}
                  onChange={miniWidgetTextInput.handleChange}
                  placeholder="Enter text"
                />
              </div>
            )}

            <div id={`${baseId}-mini-widget-show-price`}>
              <Checkbox
                label="Show 'Price'"
                checked={localConfig.miniWidgetShowPrice}
                onChange={(checked) => handleChange('miniWidgetShowPrice', checked)}
              />
            </div>

            <div id={`${baseId}-mini-widget-show-item-count`}>
              <Checkbox
                label="Show 'Item Count'"
                checked={localConfig.miniWidgetShowItemCount}
                onChange={(checked) => handleChange('miniWidgetShowItemCount', checked)}
              />
            </div>

            <div id={`${baseId}-mini-widget-on-click`}>
              <Dropdown
                label="On Click"
                labelSize="sm"
                options={MINI_WIDGET_ON_CLICK_OPTIONS}
                value={localConfig.miniWidgetOnClick}
                onChange={(value) => handleChange('miniWidgetOnClick', value)}
                disabled
              />
            </div>
          </div>
        )}

        {/* Build Configuration header (only shown when from header) */}
        {isFromHeader && (
          <div id={`${baseId}-build-config-header`} className="tw-pb-3">
            <FieldTitle className="tw-bg-surface-sunken tw-px-1 tw-py-2" label="Build Configuration" size="sm" infoMessage="Build Configuration" />
          </div>
        )}

        {/* Title Configuration */}
        <div className="tw-space-y-3">
          <Checkbox
            label="Show 'Title'"
            checked={localConfig.showTitle}
            onChange={(checked) => handleChange('showTitle', checked)}
          />

          {localConfig.showTitle && (
            <div className="tw-pl-4 tw-border-l-2 tw-border-edge">
              <TextField
                label=""
                value={titleInput.localValue}
                onChange={titleInput.handleChange}
                placeholder="Enter title"
              />
            </div>
          )}
        </div>

        {/* Share Button Configuration */}
        <div className="tw-mt-4 tw-space-y-3">
          <Checkbox
            label="Show 'Share' Button"
            checked={localConfig.showShareButton}
            onChange={(checked) => handleChange('showShareButton', checked)}
          />

          {localConfig.showShareButton && (
            <div className="tw-pl-4 tw-border-l-2 tw-border-edge tw-space-y-3">
              <Checkbox
                label="Show Text"
                checked={localConfig.shareButtonConfig.showText}
                onChange={(checked) => handleShareButtonChange('showText', checked)}
              />

              {localConfig.shareButtonConfig.showText && (
                <div className="tw-pl-4 tw-border-l-2 tw-border-edge">
                  <TextField
                    label="Button Label"
                    value={shareButtonLabelInput.localValue}
                    onChange={shareButtonLabelInput.handleChange}
                    placeholder="Enter button label"
                  />
                </div>
              )}

              <Checkbox
                label="Show Icon"
                checked={localConfig.shareButtonConfig.showIcon}
                onChange={(checked) => handleShareButtonChange('showIcon', checked)}
              />
            </div>
          )}
        </div>

        {/* Description Configuration */}
        <div className="tw-mt-4 tw-space-y-3">
          <Checkbox
            label="Show Description"
            checked={localConfig.showDescription}
            onChange={(checked) => handleChange('showDescription', checked)}
          />

          {localConfig.showDescription && (
            <div className="tw-pl-4 tw-border-l-2 tw-border-edge tw-space-y-2">
              {/* Description Title Configuration */}
              <div id={`${baseId}-desc-show-title`}>
                <Checkbox
                  label="Show Title"
                  checked={localConfig.showDescriptionTitle}
                  onChange={(checked) => handleChange('showDescriptionTitle', checked)}
                />
              </div>

              {localConfig.showDescriptionTitle && (
                <div id={`${baseId}-desc-title`} className="tw-pl-4 tw-border-l-2 tw-border-edge">
                  <TextField
                    label=""
                    value={descriptionTitleInput.localValue}
                    onChange={descriptionTitleInput.handleChange}
                    placeholder="Enter Title"
                  />
                </div>
              )}

              <div id={`${baseId}-desc-collapse-expand`}>
                <Checkbox
                  label="Collapsible"
                  checked={localConfig.descriptionTitleCollapseExpand}
                  onChange={(checked) => handleChange('descriptionTitleCollapseExpand', checked)}
                />
              </div>

              {/* Bold and Italic buttons */}
              <div className="tw-flex tw-gap-1">
                <button data-testid="configpanel-yourbuildconfigpanel-button-1"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDescriptionFormat('bold');
                  }}
                  className="tw-w-8 tw-h-8 tw-border tw-border-edge tw-rounded tw-flex tw-items-center tw-justify-center tw-bg-surface hover:tw-bg-surface-hover active:tw-bg-surface-sunken"
                >
                  <span className="tw-font-extrabold tw-text-sm tw-text-ink">B</span>
                </button>
                <button data-testid="configpanel-yourbuildconfigpanel-button-2"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDescriptionFormat('italic');
                  }}
                  className="tw-w-8 tw-h-8 tw-border tw-border-edge tw-rounded tw-flex tw-items-center tw-justify-center tw-bg-surface hover:tw-bg-surface-hover active:tw-bg-surface-sunken"
                >
                  <span className="tw-italic tw-text-sm tw-font-extrabold tw-text-ink">I</span>
                </button>
              </div>

              {/* Rich text editor */}
              <div
                ref={descriptionRef}
                contentEditable
                suppressContentEditableWarning={true}
                onInput={handleDescriptionChange}
                onBlur={handleDescriptionChange}
                className="tw-w-full tw-border tw-text-sm tw-text-ink tw-font-medium tw-border-edge tw-rounded tw-px-3 tw-py-2 tw-min-h-[100px] tw-bg-surface focus:tw-outline-none focus:tw-border-accent focus:tw-ring-1 focus:tw-ring-accent"
                style={{ wordWrap: 'break-word' }}
              />
            </div>
          )}
        </div>

        {/* Add to Cart Button Configuration */}
        <div className="tw-mt-4 tw-space-y-3">
          <Checkbox
            label="Show 'Add to Cart' Button"
            checked={localConfig.showAddToCartButton}
            onChange={(checked) => handleChange('showAddToCartButton', checked)}
          />

          {localConfig.showAddToCartButton && (
            <div className="tw-pl-4 tw-border-l-2 tw-border-edge tw-space-y-3">
              <Checkbox
                label="Show Text"
                checked={localConfig.addToCartButtonConfig.showText}
                onChange={(checked) => handleAddToCartButtonChange('showText', checked)}
              />

              {localConfig.addToCartButtonConfig.showText && (
                <div className="tw-pl-4 tw-border-l-2 tw-border-edge">
                  <TextField
                    label="Button Label"
                    value={addToCartButtonLabelInput.localValue}
                    onChange={addToCartButtonLabelInput.handleChange}
                    placeholder="Enter button label"
                  />
                </div>
              )}

              <Checkbox
                label="Show Icon"
                checked={localConfig.addToCartButtonConfig.showIcon}
                onChange={(checked) => handleAddToCartButtonChange('showIcon', checked)}
              />

              <div className="tw-flex tw-items-center">
                <Checkbox
                  label="Save to permanent storage"
                  checked={localConfig.addToCartButtonConfig.saveToPermanentStorage}
                  onChange={(checked) =>
                    handleAddToCartButtonChange('saveToPermanentStorage', checked)
                  }
                />
                <span style={{ paddingLeft: '3px' }}>
                  <InfoTooltip
                    label="About Save to permanent storage"
                    data-testid={`${baseId}-save-to-permanent-storage-info`}
                    message={
                      <>
                        Ensure <strong>‘Allow to save configurations’</strong> is enabled in Merchant Settings for this to work. This feature reflects only in e-commerce integrations and will not appear in preview.
                      </>
                    }
                    placement="top"
                  />
                </span>
              </div>

              <div className="tw-space-y-1">
                <div className="tw-flex tw-items-center tw-gap-1">
                  <span className="tw-text-sm tw-font-medium tw-text-ink">Adding to Cart Message</span>
                  <InfoTooltip
                    label="About Adding to Cart Message"
                    data-testid={`${baseId}-adding-to-cart-message-info`}
                    message="This message appears while products are being added to the cart."
                  />
                </div>
                <TextField
                  label=""
                  value={addingToCartMessageInput.localValue}
                  onChange={(value) => {
                    const sanitized = value.replace(/[^a-zA-Z0-9 .,!?;:'"()\-&/]/g, '').slice(0, 100);
                    addingToCartMessageInput.handleChange(sanitized);
                  }}
                  placeholder="Enter loading message"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default YourBuildConfigPanel;
